import styled from 'styled-components';
import * as yup from 'yup';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import StandardModal from 'components/shared/StandardModal';
import { Form, FormProvider, useForm } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { parseDate, simpleDashDate } from 'lib/date';
import Admission from 'models/reviews/Admission';
import { updateAdmission } from 'services/api/admissions';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

type GenderOption = (typeof GENDERS)[number];

type EditAdmissionFormValues = {
  patient: {
    name: string;
    dateOfBirth: Date | null;
    gender: GenderOption | null;
  };
  planType: string;
  referralSource: string;
};

type EditAdmissionFormMeta = {
  isSubmitting: boolean;
};

const editAdmissionSchema = yup.object({
  patient: yup.object({
    name: yup.string().nullable(),
    dateOfBirth: yup
      .date()
      .nullable()
      .max(new Date(), 'Date of birth cannot be in the future')
      .typeError('Enter a valid date'),
    gender: yup
      .object()
      .shape({
        label: yup.string(),
        value: yup.string(),
      })
      .nullable(),
  }),
  planType: yup.string().nullable(),
  referralSource: yup.string().nullable(),
});

export type EditAdmissionPatientModalProps = {
  admission: Admission;
  onCancel: () => void;
};

export default function EditAdmissionPatientModal({ admission, onCancel }: EditAdmissionPatientModalProps) {
  const patient = admission.data.patient ?? {};
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate } = useMutateWithToast({
    mutationFn: updateAdmission,
  });

  const handleSubmit = (
    values: FormValues<EditAdmissionFormValues>,
    _meta: FormMeta<EditAdmissionFormMeta>,
    helpers: FormHelpers<EditAdmissionFormValues, EditAdmissionFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    mutate(
      {
        id: admission.id,
        patient: {
          name: values.patient?.name ?? '',
          dateOfBirth: simpleDashDate(values.patient?.dateOfBirth) ?? '',
          gender: values.patient?.gender?.value ?? '',
        },
        planType: values.planType ?? '',
        referralSource: values.referralSource ?? '',
      },
      {
        onSuccess: () => {
          addToast({ text: 'Patient information was successfully updated!' });
          invalidateQueries({ admissions: true });
          onCancel();
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  return (
    <StandardModal onCancel={onCancel} title='Edit Patient Information'>
      <FormProvider<EditAdmissionFormValues, EditAdmissionFormMeta>
        onSubmit={handleSubmit}
        yupSchema={editAdmissionSchema}
        initialValues={{
          patient: {
            name: patient.name ?? '',
            dateOfBirth: parseDate(patient.dateOfBirth ?? null),
            gender: GENDERS.find((g) => g.value === patient.gender) ?? null,
          },
          planType: admission.data.planType ?? '',
          referralSource: admission.data.referralSource ?? '',
        }}
        initialMeta={{ isSubmitting: false }}>
        <EditAdmissionPatientForm onCancel={onCancel} />
      </FormProvider>
    </StandardModal>
  );
}

function EditAdmissionPatientForm({ onCancel }: { onCancel: () => void }) {
  const { values, errors, setValue, validateField, valid, dirty, meta } = useForm<
    EditAdmissionFormValues,
    EditAdmissionFormMeta
  >();

  const isSubmitting = meta.isSubmitting;

  return (
    <Form>
      <Content>
        <InputGroup>
          <BodySmall>Patient Name</BodySmall>
          <Input
            type='text'
            value={values.patient?.name ?? ''}
            onChange={(e) => setValue('patient.name', e.target.value)}
            placeholder='Enter patient name'
            disabled={isSubmitting}
          />
        </InputGroup>

        <InputGroup>
          <BodySmall>Date of Birth</BodySmall>
          <DatePicker
            selected={values.patient?.dateOfBirth ?? null}
            onChange={(date) => setValue('patient.dateOfBirth', date)}
            onBlur={() => validateField('patient.dateOfBirth', values)}
            maxDate={new Date()}
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
            $hasError={!!errors.patient?.dateOfBirth}
            $fullWidth
            disabled={isSubmitting}
          />
          {errors.patient?.dateOfBirth && <ErrorText>{errors.patient.dateOfBirth}</ErrorText>}
        </InputGroup>

        <InputGroup>
          <BodySmall>Gender</BodySmall>
          <Select
            dataTestId='gender-select'
            placeholder='Select Gender'
            name='gender'
            value={values.patient?.gender ?? null}
            onChange={(option) => setValue('patient.gender', option)}
            hasError={!!errors.patient?.gender}
            options={GENDERS}
            disabled={isSubmitting}
            getOptionValue={(g) => g.value}
            getOptionLabel={(g) => g.label}
          />
          {errors.patient?.gender && <ErrorText>{errors.patient.gender}</ErrorText>}
        </InputGroup>

        <InputGroup>
          <BodySmall>Plan Type</BodySmall>
          <Input
            type='text'
            value={values.planType ?? ''}
            onChange={(e) => setValue('planType', e.target.value)}
            placeholder='Enter plan type'
            disabled={isSubmitting}
          />
        </InputGroup>

        <InputGroup>
          <BodySmall>Referral Source</BodySmall>
          <Input
            type='text'
            value={values.referralSource ?? ''}
            onChange={(e) => setValue('referralSource', e.target.value)}
            placeholder='Enter referral source'
            disabled={isSubmitting}
          />
        </InputGroup>
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' type='button' onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type='submit' disabled={!valid || !dirty} loading={isSubmitting}>
          Update
        </Button>
      </StandardModal.Actions>
    </Form>
  );
}

const Content = styled.div`
  margin: 16px 0 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorText = styled(BodySmall)`
  color: var(--accent-red);
  margin-top: 4px;
`;
